(() => {
  const purchaseBars = new WeakSet();
  const prepareMobilePurchaseBar = async (bar) => {
    if (purchaseBars.has(bar)) return;
    purchaseBars.add(bar);
    await customElements.whenDefined('sticky-add-to-cart');
    await bar.updateComplete;
    if (!bar.isConnected || typeof bar.handleAddToCartIntersection !== 'function') return;

    // Rebind after the native component's first render. The base observer can
    // miss its initial target or retain an old button after a section morph.
    // Delegate visibility to the original controller; purchase/variant logic,
    // expanded-header guards and cart eligibility remain entirely native.
    let target = null;
    const observer = new IntersectionObserver(entries => {
      if (matchMedia('(max-width: 767px)').matches && entries[0]) {
        bar.handleAddToCartIntersection(entries[0]);
      }
    }, {rootMargin: '-20% 0px 0px 0px'});
    const bindTarget = () => {
      if (!bar.isConnected) {
        observer.disconnect();
        changes.disconnect();
        return;
      }
      const next = document.getElementById(bar.getAttribute('target'));
      if (next === target) return;
      observer.disconnect();
      target = next;
      if (target) observer.observe(target);
    };
    const changes = new MutationObserver(bindTarget);
    const section = bar.closest('[id^="shopify-section-"]');
    if (section) changes.observe(section, {childList: true, subtree: true});
    bindTarget();

    // A fast scroll may skip the button completely: the native observer stays
    // non-intersecting and does not emit a new entry. Refresh its same geometry
    // decision once per frame on mobile without replacing the native strip.
    let scheduled = false;
    const refreshVisibility = () => {
      scheduled = false;
      if (!bar.isConnected) {
        observer.disconnect();
        changes.disconnect();
        window.removeEventListener('scroll', scheduleRefresh);
        window.removeEventListener('resize', scheduleRefresh);
        return;
      }
      if (!matchMedia('(max-width: 767px)').matches) return;
      bindTarget();
      if (!target) return;
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Percentage IntersectionObserver root margins are based on root width.
      const rootTop = document.documentElement.clientWidth * .2;
      const isIntersecting = rect.bottom > rootTop && rect.top < innerHeight;
      bar.handleAddToCartIntersection({isIntersecting, boundingClientRect: rect});
    };
    const scheduleRefresh = () => {
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(refreshVisibility);
      }
    };
    window.addEventListener('scroll', scheduleRefresh, {passive: true});
    window.addEventListener('resize', scheduleRefresh, {passive: true});
    scheduleRefresh();

    const mobile = bar.querySelector('sticky-add-to-cart-mobile');
    if (mobile) {
      await customElements.whenDefined('sticky-add-to-cart-mobile');
      await mobile.updateComplete;
      const close = mobile.shadowRoot?.querySelector('.close-button');
      if (close && !close.hasAttribute('aria-label')) close.setAttribute('aria-label', 'Close product options');
    }
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('sticky-add-to-cart').forEach(prepareMobilePurchaseBar);
    scope.querySelectorAll('[data-pj-page-content]').forEach(content => {
      if (content.dataset.pjPrepared) return;
      content.dataset.pjPrepared = 'true';
      const reportTables = [...content.querySelectorAll('.pj-depth-report-table')];
      reportTables.forEach(wrapper => {
        const table = wrapper.querySelector('table');
        const rows = table ? [...table.rows] : [];
        const singleColumn = rows.length > 0 && rows.every(row =>
          [...row.cells].reduce((columns, cell) => columns + cell.colSpan, 0) <= 1);
        wrapper.classList.toggle('pj-depth-report-table--single-column', singleColumn);
        if (singleColumn) wrapper.removeAttribute('tabindex');
      });
      if (reportTables.length && reportTables.every(wrapper => wrapper.classList.contains('pj-depth-report-table--single-column'))) {
        const tableHelp = content.querySelector('.pj-depth-table-help');
        if (tableHelp) tableHelp.hidden = true;
      }
      const toc = content.closest('.pj-studio-page-layout')?.querySelector('[data-pj-page-toc]');
      content.querySelectorAll('h2').forEach((heading, index) => {
        if (!heading.id) heading.id = 'pj-information-' + index;
        if (!toc) return;
        const link = document.createElement('a');
        link.href = '#' + heading.id;
        link.textContent = heading.textContent;
        toc.append(link);
      });
      if (toc && !toc.children.length) toc.closest('aside').hidden = true;
      content.querySelectorAll('.accordion-item').forEach(item => {
        const button = item.querySelector('.accordion-button');
        const panel = item.querySelector('.accordion-collapse');
        if (!button || !panel) return;
        const expanded = panel.classList.contains('show');
        button.removeAttribute('data-bs-toggle');
        button.removeAttribute('data-bs-target');
        panel.removeAttribute('data-bs-parent');
        panel.classList.remove('collapse');
        button.setAttribute('aria-controls', panel.id);
        button.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(open));
          panel.hidden = !open;
        });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
